import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import {
  AddressFields,
  FIELD_CLASS,
  type AddressValue,
} from '@/components/properties/AddressFields';
import { useUpdateProperty } from '@/routes/properties/useProperty';
import type { Property } from '@/routes/customers/useCustomers';

/**
 * "Edit property" — M1-05's scope extension: `PUT /properties/{id}` shipped
 * API-only in M1-02, and the detail page is where you'd edit from.
 *
 * `CustomerId` has no field here, matching the API: reassigning a property to
 * a different customer isn't in this story's AC, and the property party links
 * (owner/tenant/manager) would need their own migration story to follow it.
 *
 * The address block is `AddressFields` (M1-23), the same one the add dialog
 * uses. An address change makes the API re-geocode on save; a change to the
 * gate code or notes does not, and the property page's Location block shows
 * whichever outcome the save produced.
 */
export function EditPropertyDialog({
  property,
  onClose,
  onSaved,
}: {
  property: Property;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const updateProperty = useUpdateProperty(property.id);

  const [address, setAddress] = useState<AddressValue>({
    line1: property.address.line1,
    line2: property.address.line2 ?? '',
    city: property.address.city,
    state: property.address.state,
    postalCode: property.address.postalCode,
  });
  const [unit, setUnit] = useState(property.unit ?? '');
  const [accessNotes, setAccessNotes] = useState(property.accessNotes ?? '');
  const [gateCode, setGateCode] = useState(property.gateCode ?? '');
  const [notes, setNotes] = useState(property.notes ?? '');

  const canSubmit =
    address.line1.trim() !== '' &&
    address.city.trim() !== '' &&
    address.state.trim() !== '' &&
    address.postalCode.trim() !== '';

  function submit() {
    updateProperty.mutate(
      {
        address: {
          line1: address.line1.trim(),
          line2: address.line2.trim() || null,
          city: address.city.trim(),
          state: address.state.trim(),
          postalCode: address.postalCode.trim(),
        },
        unit: unit.trim() || null,
        accessNotes: accessNotes.trim() || null,
        gateCode: gateCode.trim() || null,
        notes: notes.trim() || null,
      },
      {
        onSuccess: () => {
          onSaved();
          onClose();
        },
      },
    );
  }

  const textarea =
    'w-full rounded-md border border-[var(--color-input)] bg-transparent px-2 py-1.5 text-sm focus-visible:ring-2 focus-visible:ring-[var(--color-ring)] focus-visible:outline-none';

  return (
    <Dialog
      titleId="edit-property-title"
      title={t('propertyDetail.editProperty.title')}
      onClose={onClose}
      testId="edit-property-dialog"
      dismissLabel={t('propertyDetail.editProperty.dismiss')}
      footer={
        <>
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            type="button"
            size="sm"
            disabled={!canSubmit || updateProperty.isPending}
            onClick={submit}
          >
            {updateProperty.isPending
              ? t('propertyDetail.editProperty.saving')
              : t('propertyDetail.editProperty.confirm')}
          </Button>
        </>
      }
    >
      <AddressFields
        value={address}
        onChange={setAddress}
        labels={{
          line1: t('propertyDetail.editProperty.line1'),
          line2: t('propertyDetail.editProperty.line2'),
          city: t('propertyDetail.editProperty.city'),
          state: t('propertyDetail.editProperty.state'),
          postalCode: t('propertyDetail.editProperty.postalCode'),
        }}
      >
        <label className="block text-sm">
          <span className="mb-1 block font-medium">{t('propertyDetail.editProperty.unit')}</span>
          <input
            className={FIELD_CLASS}
            value={unit}
            onChange={(event) => setUnit(event.target.value)}
          />
        </label>
      </AddressFields>

      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('propertyDetail.editProperty.gateCode')}</span>
        <input
          className={FIELD_CLASS}
          value={gateCode}
          onChange={(event) => setGateCode(event.target.value)}
        />
      </label>

      <label className="block text-sm">
        <span className="mb-1 block font-medium">
          {t('propertyDetail.editProperty.accessNotes')}
        </span>
        <textarea
          rows={2}
          className={textarea}
          value={accessNotes}
          onChange={(event) => setAccessNotes(event.target.value)}
        />
      </label>

      <label className="block text-sm">
        <span className="mb-1 block font-medium">{t('propertyDetail.editProperty.notes')}</span>
        <textarea
          rows={2}
          className={textarea}
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
        />
      </label>

      {updateProperty.isError ? (
        <p role="alert" className="text-sm text-[var(--color-destructive)]">
          {t('propertyDetail.editProperty.failed')}
        </p>
      ) : null}
    </Dialog>
  );
}
